// SPDX-License-Identifier: MIT
import type { Brand } from '@pulsereel/schema';
import { type Box, type Subpath, bounds, parsePath } from './kit/path.ts';
export type BrandLogo = {
    /** All outlines, flattened, in viewBox units. */
    subpaths: Subpath[];
    /** Per source path: outlines plus fill colour, role (e.g. "wordmark", "symbol") and fill rule. */
    parts: Array<{
        subpaths: Subpath[];
        fill: string | undefined;
        role: string | undefined;
        fillRule: CanvasFillRule;
    }>;
    /** Fill rule for the whole mark: pass it to `ctx.fill()` so counters (O, D) stay open. */
    fillRule: CanvasFillRule;
    viewBox: Box;
    /** Tight bounds of the outlines. */
    bounds: Box;
};
export type ResolvedBrand = {
    name: string;
    colors: {
        primary: string;
        accent: string;
        ink: string;
        paper: string;
    };
    fonts: {
        display: string;
        cjk: string;
        mono: string;
    };
    tagline: string | undefined;
    logo: BrandLogo | undefined;
};
function resolveLogo(logo: NonNullable<Brand['logo']>): BrandLogo {
    const parts = logo.paths.map((p) => ({
        subpaths: parsePath(p.d),
        fill: p.fill,
        role: p.role,
        fillRule: p.fillRule ?? 'nonzero',
    }));
    const subpaths = parts.flatMap((p) => p.subpaths);
    const [x, y, w, h] = logo.viewBox;
    const fillRule: CanvasFillRule = parts.some((p) => p.fillRule === 'evenodd') ? 'evenodd' : 'nonzero';
    return { subpaths, parts, viewBox: { x, y, w, h }, bounds: bounds(subpaths), fillRule };
}
const FALLBACK = { primary: '#3B5BFF', accent: '#22D3EE', ink: '#0B0D12', paper: '#F5F7FB' };
export function resolveBrand(brand: Brand): ResolvedBrand {
    const colors = { ...FALLBACK };
    for (const { role, hex } of brand.palette)
        colors[role] = hex;
    return {
        name: brand.name,
        colors,
        fonts: { display: brand.fonts.display, cjk: brand.fonts.cjk, mono: brand.fonts.mono },
        tagline: brand.copy.tagline,
        logo: brand.logo ? resolveLogo(brand.logo) : undefined,
    };
}
/** CSS font shorthand with CJK fallback after the Latin display face. */
export function font(brand: ResolvedBrand, weight: number, px: number, family: 'display' | 'mono' = 'display'): string {
    const primary = family === 'mono' ? brand.fonts.mono : brand.fonts.display;
    return `${weight} ${px}px "${primary}", "${brand.fonts.cjk}", sans-serif`;
}
/** `#RRGGBB` + alpha → rgba() string. */
export function rgba(hex: string, alpha: number): string {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
